/* eslint-disable class-methods-use-this */

/**
 * Blocks Amplitude's attribution $identify calls during Klarna returns.
 *
 * When Klarna redirects back, Amplitude's attribution plugin may overwrite original
 * UTM/referral data by setting properties based on the current URL (which lacks attribution
 * params). This plugin intercepts and blocks those attribution $identify events
 * (utm_*, referrer, etc.) while allowing manual identify calls (e.g., signup_utm_*) to pass through.
 *
 * Only registered (in main.jsx) during Klarna returns (when setup_intent params are present on /join/payment).
 */
class AttributionPreservationPlugin {
    name = 'attribution-preservation';

    // @amplitude/analytics-types not installed, so we need to use a string
    type = 'enrichment';

    /**
     * Checks if user properties contain any attribution-related properties.
     *
     * Examines the `$set`, `$setOnce`, and `$unset` properties to determine if any
     * attribution properties are present.
     *
     * @param {Object} userProperties - The user properties object from an Amplitude event,
     *   which may contain `$set`, `$setOnce`, and/or `$unset` properties.
     * @returns {boolean} Returns `true` if any attribution properties are found in the
     *   user properties, `false` otherwise. Also returns `false` if `userProperties` is
     *   null, undefined, or falsy.
     */
    hasAttributionProperties(userProperties) {
        if (!userProperties) {
            return false;
        }

        const checkProps = (props) =>
            Object.keys(props || {}).some((prop) => {
                const isAttribution =
                    prop.startsWith('utm_') ||
                    prop.startsWith('initial_utm_') ||
                    prop === 'referrer' ||
                    prop === 'initial_referrer' ||
                    prop === 'referring_domain' ||
                    prop === 'initial_referring_domain';

                return isAttribution;
            });

        return (
            checkProps(userProperties.$set) || checkProps(userProperties.$setOnce) || checkProps(userProperties.$unset)
        );
    }

    async execute(event) {
        if (event.event_type !== '$identify') {
            return event;
        }

        if (this.hasAttributionProperties(event.user_properties)) {
            // return null to prevent the event from being sent to Amplitude
            return null;
        }

        return event;
    }
}

export { AttributionPreservationPlugin };
